import { useCallback, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useFeld } from '@/lib/speichern'
import {
  createTimeStandard,
  deleteTimeStandard,
  fetchCalcPhases,
  fetchCalcQualifications,
  fetchTimeStandards,
  timeStandardsFromControlling,
} from '../api/calculation-settings.api'
import { useZeilenStelle } from '../lib/kalkulation-einstellungen'
import type { CalcPhase, CalcQualification, CalcTimeStandard, CalcTimeStandardProposal } from '../types/calculation.types'
import { ZellenFeld, ZellenHaken, knopfPrimaer, tabellenKopf } from './EinstellungsBausteine'

const FEHLER = 'Die Richtwerte ließen sich nicht laden oder speichern.'
const zahl = (n: number, stellen = 4) => n.toLocaleString('de-DE', { maximumFractionDigits: stellen })
const leer = { activityName: '', referenceName: '', referenceUnitCode: '', hoursPerReference: '' }

/**
 * AP-37 Detailblatt N 6.5 — **Richtwerte** (`.CORE`, ohne KI): Stunden je Bezugsgröße des
 * Betriebs („Steher anschweißen — 0,25 h je Stk"). Im Blatt entstehen daraus Stunden mit
 * Rechenweg („aus Richtwert"). Symphenia liefert keinen Startkatalog (KI-16); mit
 * `PROJECT_MGMT.CONTROLLING` schlägt die Nachkalkulation Werte vor (Median ab drei Ist-Werten).
 */
export function RichtwerteEinstellungen({ darfAendern }: { darfAendern: boolean }) {
  const { hasModule } = useAuthStore()
  const [zeilen, setZeilen] = useState<CalcTimeStandard[]>([])
  const [phasen, setPhasen] = useState<CalcPhase[]>([])
  const [qualis, setQualis] = useState<CalcQualification[]>([])
  const [neu, setNeu] = useState(leer)
  const [vorschlaege, setVorschlaege] = useState<CalcTimeStandardProposal[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(() => {
    void fetchTimeStandards(true)
      .then(setZeilen)
      .catch((e) => setFehler(apiErrorText(e, FEHLER)))
  }, [])
  useEffect(() => {
    laden()
    fetchCalcPhases().then(setPhasen).catch(() => setPhasen([]))
    fetchCalcQualifications().then(setQualis).catch(() => setQualis([]))
  }, [laden])

  const schritt = async (tu: () => Promise<unknown>) => {
    try {
      setFehler(null)
      await tu()
      laden()
    } catch (e) {
      setFehler(apiErrorText(e, FEHLER))
    }
  }
  const stunden = Number(neu.hoursPerReference.replace(',', '.'))
  const neuGueltig = !!neu.activityName.trim() && !!neu.referenceName.trim() && !!neu.referenceUnitCode.trim() && Number.isFinite(stunden) && stunden >= 0 && neu.hoursPerReference.trim() !== ''

  return (
    <div className={`${formCardSm} max-w-5xl`}>
      <h3 className={cardTitle}>Richtwerte</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Stunden je Bezugsgröße aus der Erfahrung des Betriebs. Im Blatt: Arbeitsschritt „aus Richtwert" — Stunden = Menge der Bezugsgröße × Richtwert, mit
        Rechenweg. Symphenia liefert keine Werte aus.
      </p>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={tabellenKopf}>
              <th className="py-1.5 pr-2">Tätigkeit</th>
              <th className="py-1.5 pr-2">Bezugsgröße</th>
              <th className="py-1.5 pr-2">Einheit</th>
              <th className="py-1.5 pr-2 text-right">h je Einheit</th>
              <th className="py-1.5 pr-2">Qualifikation</th>
              <th className="py-1.5 pr-2">Phase</th>
              <th className="py-1.5 pr-2">Quelle</th>
              <th className="py-1.5 pr-2">aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <RichtwertZeile
                key={z.id}
                zeile={z}
                phasen={phasen}
                qualis={qualis}
                darf={darfAendern}
                onLoeschen={() =>
                  void bestaetige(`Richtwert „${z.activityName} — ${z.referenceName}" löschen? Arbeitsschritte behalten ihre Stunden.`, { ok: 'Löschen', gefaehrlich: true }).then(
                    (ja) => {
                      if (ja) void schritt(() => deleteTimeStandard(z.id))
                    },
                  )
                }
              />
            ))}
            {zeilen.length === 0 && (
              <tr>
                <td colSpan={9} className="py-3 text-center text-xs text-muted-foreground">
                  Noch keine Richtwerte.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {darfAendern && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input className={`${inlineInput} !w-56`} placeholder="Tätigkeit" value={neu.activityName} onChange={(e) => setNeu({ ...neu, activityName: e.target.value })} />
          <input className={`${inlineInput} !w-48`} placeholder="Bezugsgröße, z. B. Steher" value={neu.referenceName} onChange={(e) => setNeu({ ...neu, referenceName: e.target.value })} />
          <input className={`${inlineInput} !w-20`} placeholder="Einheit" value={neu.referenceUnitCode} onChange={(e) => setNeu({ ...neu, referenceUnitCode: e.target.value })} />
          <input
            className={`${inlineInput} !w-28 text-right`}
            inputMode="decimal"
            placeholder="h je Einheit"
            value={neu.hoursPerReference}
            onChange={(e) => setNeu({ ...neu, hoursPerReference: e.target.value })}
          />
          <button
            type="button"
            className={knopfPrimaer}
            disabled={!neuGueltig}
            onClick={() =>
              schritt(() =>
                createTimeStandard({
                  activityName: neu.activityName.trim(),
                  referenceName: neu.referenceName.trim(),
                  referenceUnitCode: neu.referenceUnitCode.trim(),
                  hoursPerReference: stunden,
                }).then(() => setNeu(leer)),
              )
            }
          >
            Anlegen
          </button>
          {hasModule('PROJECT_MGMT.CONTROLLING') && (
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1.5 text-sm"
              onClick={() =>
                void timeStandardsFromControlling()
                  .then(setVorschlaege)
                  .catch((e) => setFehler(apiErrorText(e, FEHLER)))
              }
            >
              Aus Nachkalkulation vorschlagen
            </button>
          )}
        </div>
      )}
      {vorschlaege && (
        <div className="mt-4 rounded-md border border-border/60 p-3">
          <div className="mb-1 text-sm font-medium">Vorschläge aus der Nachkalkulation</div>
          <p className="mb-2 text-xs text-muted-foreground">
            Median der Ist-Stunden je Einheit abgeschlossener Positionen, ab drei Werten. Übernommen wird nur, was du anlegst.
          </p>
          {vorschlaege.length === 0 ? (
            <p className="text-xs text-muted-foreground">Keine Tätigkeit mit mindestens drei Ist-Werten.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={tabellenKopf}>
                  <th className="py-1 pr-2">Tätigkeit</th>
                  <th className="py-1 pr-2">Einheit</th>
                  <th className="py-1 pr-2 text-right">Median h/E</th>
                  <th className="py-1 pr-2 text-right">Spanne</th>
                  <th className="py-1 pr-2 text-right">Werte</th>
                  <th className="py-1 pr-2">vorhanden</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {vorschlaege.map((v) => (
                  <tr key={`${v.activityName}|${v.referenceUnitCode}`} className="border-b border-border/60">
                    <td className="py-1 pr-2">{v.activityName}</td>
                    <td className="py-1 pr-2">{v.referenceUnitCode}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{zahl(v.hoursPerReference)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">
                      {zahl(v.min)} – {zahl(v.max)}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{v.basedOnCount}</td>
                    <td className="py-1 pr-2 text-xs text-muted-foreground">{v.existing ? `${zahl(v.existing.hoursPerReference)} h/E` : '—'}</td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() =>
                          schritt(() =>
                            createTimeStandard({
                              activityName: v.activityName,
                              referenceName: v.referenceName,
                              referenceUnitCode: v.referenceUnitCode,
                              hoursPerReference: v.hoursPerReference,
                              source: 'CONTROLLING',
                              basedOnCount: v.basedOnCount,
                            }).then(() => setVorschlaege((alt) => alt?.filter((x) => x !== v) ?? null)),
                          )
                        }
                      >
                        Anlegen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

function RichtwertZeile({
  zeile: z,
  phasen,
  qualis,
  darf,
  onLoeschen,
}: {
  zeile: CalcTimeStandard
  phasen: CalcPhase[]
  qualis: CalcQualification[]
  darf: boolean
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcTimeStandard', `/calculation-settings/time-standards/${z.id}`, z, `${z.activityName} — ${z.referenceName}`)
  const stunden = useFeld<number | null>(stelle, 'hoursPerReference', { art: 'zahl', stellen: 4, label: 'h je Einheit', pflicht: true, gesperrt: !darf })
  const quali = useFeld<string | null>(stelle, 'qualificationCode', { art: 'auswahl', label: 'Qualifikation', gesperrt: !darf })
  const phase = useFeld<string | null>(stelle, 'phaseKey', { art: 'auswahl', label: 'Phase', gesperrt: !darf })
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="activityName" label="Tätigkeit" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="referenceName" label="Bezugsgröße" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="referenceUnitCode" label="Einheit" darf={darf} klasse="w-16" pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <span className="flex items-center gap-1">
          <input className={`${inlineInput} w-24 text-right`} inputMode="decimal" aria-label="h je Einheit" {...stunden.props} />
          <FeldStatusZeichen status={stunden} />
        </span>
        <FeldStatus status={stunden} zeichen={false} />
      </td>
      <td className="py-1.5 pr-2">
        <select className={`${inlineInput} w-32`} aria-label="Qualifikation" {...quali.props}>
          <option value="">—</option>
          {qualis.map((q) => (
            <option key={q.id} value={q.code}>
              {q.name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1.5 pr-2">
        <select className={`${inlineInput} w-32`} aria-label="Phase" {...phase.props}>
          <option value="">—</option>
          {phasen.map((p) => (
            <option key={p.id} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{z.source === 'CONTROLLING' ? `Nachkalkulation (${z.basedOnCount ?? '?'})` : 'von Hand'}</td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="löschen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}
